/**
 * "Dealer talk": the same messages in two voices.
 *   vegas: a wisecracking Las Vegas dealer (the default). Spicy PG-13: teases the cards and the luck, never the person.
 *   plain: clear and simple
 *
 * Each player picks their own voice in the table Menu; it only changes wording, never rules or buttons.
 * All the Vegas lines live here so they're easy to make milder or spicier.
 */

export type DealerTalk = 'plain' | 'vegas';

const KEY = 'euchre.dealerTalk';

export function storedDealerTalk(): DealerTalk {
  try {
    // Vegas unless this player picked Plain.
    return localStorage.getItem(KEY) === 'plain' ? 'plain' : 'vegas';
  } catch {
    return 'vegas';
  }
}

export function storeDealerTalk(v: DealerTalk): void {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* private mode: lasts for this tab */
  }
}

/** Pick a line that stays the same while the game state is the same (so it doesn't flicker on every redraw). */
export function pick(lines: readonly string[], seed: string): string {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return lines[(h >>> 0) % lines.length]!;
}

export const vegas = {
  turnBadge: [
    'You’re up', 'Showtime', 'Your move, ace', 'Action’s on you', 'All eyes on you', 'Clock’s ticking', 'Big moment, kid',
    'Don’t be shy', 'Your turn, champ', 'Make it count', 'No pressure',
  ],

  lead: (verb: string) => [
    `Your lead. Make it count. Or don’t, I get paid either way. ${verb} any card.`,
    `The floor is yours, hotshot. ${verb} any card and try to look confident.`,
    `You’re leading. No pressure, the whole table is just staring at you. ${verb} any card.`,
    `Lead off, sport. ${verb} any card; the crowd’s getting restless.`,
    `Your lead. Pick a card like you’ve done this before. ${verb} any card.`,
    `Your lead, big shooter. Show us what you’re working with. ${verb} any card.`,
    `You’re on top this time. Set the pace. ${verb} any card.`,
    `Lead us somewhere nice, partner. ${verb} any card.`,
  ],
  play: (verb: string) => [
    `${verb} a card. Any time this century.`,
    `Go on, ${verb.toLowerCase()} a card. The table’s not getting any younger.`,
    `${verb} a card, high roller. Dazzle us.`,
    `${verb} a card. Today, preferably.`,
    `${verb} a card and pretend you planned this all along.`,
    `Go ahead, lay one down. The suspense is killing us.`,
    `${verb} a card, friend. Nobody’s getting any younger and the drinks are watered down.`,
    `${verb} a card. Confidence is good. Speed is better.`,
  ],
  follow: (suit: string, verb: string) => [
    `${suit} were led, chief. ${verb} a bright card; the grey ones are just for decoration.`,
    `Follow ${suit}, pal. The grey cards are off the menu tonight.`,
    `It’s ${suit} or nothing, champ. Bright cards only, house rules.`,
    `Rules are rules, buttercup: ${suit} only. The grey ones are benched.`,
    `${suit} were led. ${verb} a bright card; the grey ones are just here for moral support.`,
    `${suit} were led, ace. Stick with the suit; the grey ones aren’t invited.`,
    `Follow ${suit}. I don’t make the rules. I just enforce ’em with a smile.`,
  ],
  discard: (card: string, verb: string) => [
    `You grabbed the ${card}. Now ${verb.toLowerCase()} something to toss. Nobody likes a hoarder.`,
    `Nice pickup. ${verb} a card to throw back and act like you meant it.`,
    `${verb} a card to bury. We won’t tell anybody.`,
    `One of these has to go, champ. ${verb} a card to discard.`,
    `One card has to go. Break it to ’em gently. ${verb} it.`,
  ],
  orderUp: (card: string, suit: string, dealer: boolean) =>
    dealer
      ? [
          `Pick up the ${card} and make ${suit} trump? Your call, big shot.`,
          `The ${card} is right there, dealer. Scoop it up and make ${suit} trump?`,
          `Your deal, your ${card}. Take it and make ${suit} trump?`,
          `That ${card} is giving you eyes, dealer. Take it home and make ${suit} trump?`,
        ]
      : [
          `Make the dealer pick up the ${card} and call ${suit} trump? Feeling lucky?`,
          `Order up the ${card} and make ${suit} trump? Go big or go home.`,
          `Tell the dealer to grab the ${card} and make ${suit} trump? Bold. I like it.`,
          `Send the ${card} to the dealer and make ${suit} trump? Buy it a drink first.`,
        ],
  nameTrump: (out: string) => [
    `Name trump (anything but ${out}) or pass. Choose wisely… or don’t.`,
    `${out} are dead to us. Pick a new trump or pass, and make it snappy.`,
    `${out} got turned down harder than a prom date. Name a new trump or pass.`,
    `${out} got turned down. Name a new trump or pass; no take-backs.`,
    `${out} are out. Plenty of other fish. Name one or pass.`,
  ],
  stuck: [
    'Stick the dealer, pal. You’re naming trump whether you like it or not.',
    'Nobody wanted it, so it’s all yours, dealer. Name trump.',
    'Everybody chickened out. Congratulations, dealer, you’re naming trump.',
    'Stuck, dealer. No more playing hard to get. Name trump.',
  ],
  defendAlone: (maker: string, suit: string, alone: boolean, points: number) => [
    `${maker} called ${suit}${alone ? ' and is going solo' : ''}. Brave enough to defend alone? Your partner sits this one out, and stopping them alone is ${points} points of pure bragging rights.`,
    `${maker} called ${suit}${alone ? ' and wants to go solo' : ''}. Want to go it alone and stop them? Your partner grabs a snack, and a lone euchre is worth ${points} points.`,
  ],

  waiting: {
    deal: [
      'Checking the deal for funny business. Nobody touch anything.',
      'Counting the cards. Twice. This is a classy joint.',
      'Shuffling. Keep your hands where I can see ’em.',
    ],
    bid: (who: string) => [
      `${who} is deciding. Bless their heart…`,
      `${who} is thinking about trump. Don’t rush genius…`,
      `${who} is staring at their cards like they owe them money…`,
      `${who} is doing the math. Give ’em a minute…`,
      `${who} is weighing their options. All two of them…`,
      `${who} is playing hard to get…`,
      `${who} is thinking it over. We’ll wait. We always wait…`,
    ],
    defend: (who: string) => [`${who} is deciding whether to go it alone. Brave or foolish, we’ll see…`, `${who} is feeling brave. Or reckless. Stand by…`],
    discard: (who: string, card: string) => [
      `${who} grabbed the ${card} and is pretending to know what to throw away…`,
      `${who} is deciding which card gets voted off the island…`,
      `${who} is picking which card to dump. Breakups are hard…`,
    ],
    bot: (who: string) => [
      `${who}’s crunching numbers. It’s a bot; it lives for this…`,
      `${who} is consulting its circuits…`,
      `${who} is thinking. Well, “thinking.” It’s a bot…`,
      `${who} is running the odds. Bots never bluff… probably…`,
      `${who} is buffering…`,
      `${who} is warming up its processor. Things are getting steamy in there…`,
      `${who} is a bot. It doesn’t sweat, but this one’s close…`,
    ],
    person: (who: string) => [
      `${who} is thinking. Or napping. Hard to tell…`,
      `Waiting on ${who}. Somebody nudge them…`,
      `${who} is up. Hum something while you wait…`,
      `${who} is taking their sweet time…`,
      `Somebody tell ${who} the meter’s running…`,
      `${who} is up. Probably texting their ex…`,
      `${who} is thinking so hard you can hear it…`,
    ],
  },

  takesIt: (who: string, you: boolean) =>
    you
      ? [`You rake it in`, `Yours. Cha-ching`, `All yours, champ`, `You take it home`, `Nice. Yours`]
      : [`${who} rakes it in`, `${who} takes it. Cha-ching`, `${who} scoops it up`, `${who} wins that one. Rude`, `${who} takes it home`],

  result: {
    euchreUs: [
      'Euchred! The house thanks you for your donation.', 'Euchred. Well, damn. That’ll leave a mark.', 'Euchred. Let’s never speak of this again.',
      'Euchred. That’ll leave a mark, and not the fun kind.',
    ],
    euchreThem: [
      'Euchred them! Somebody call a medic.', 'You euchred ’em! Pour one out for the other team.', 'Euchred ’em! That’s gonna sting in the morning.',
      'Euchred ’em! Go ahead, do a little dance.',
    ],
    marchUs: ['A march! Somebody call security.', 'All five tricks. Show-off.', 'All five! Somebody check your sleeves.', 'All five! Somebody get these folks a cigarette.'],
    marchThem: ['They took all five. Yikes.', 'A march for them. Brutal.', 'Swept. Clean sweep. Ouch.', 'All five for them. You got worked over.'],
    madeUs: ['Hand made. Not pretty, but it counts.', 'Made it. Barely legal, but legal.', 'Got it done. Ugly wins still count.', 'Made it. Nobody’s complaining.'],
    madeThem: ['They made it. Barely.', 'They got their points. Don’t let it go to their heads.', 'They squeaked by. Sheer dumb luck, obviously.', 'They got it done. Nobody’s impressed.'],
  },
  stillThere: ['Hey! You still with us?', 'Yoo-hoo! Anybody home?', 'Earth to you!', 'Did you fall in?'],
  dealing: ['Shuffling up and dealing…', 'Fresh cards coming. Try to look surprised…', 'New hand, new excuses…', 'Fresh deck. Fresh start. Same old you…'],
  win: [
    'Winner winner, chicken dinner!', 'You win! Drinks are on… well, not the house.', 'You win! Act humble. Or don’t.',
    'You win! Somebody’s getting lucky tonight.',
  ],
  lose: [
    'The house always wins. Tonight the house was them.', 'They got you. Hey, there’s always the next game.', 'Tough loss. The cards were rigged. Allegedly.',
    'You lost. Happens to the best of us. Mostly to you, tonight.',
  ],
};
